import { sendEvent } from '@concerns/monitoring/store/monitoringSlice'
import { PayloadAction } from '@reduxjs/toolkit'
import { captureException } from '@sentry/nextjs'
import { EngineCapturePayload } from '@services/engine/types'
import Context from '@store/middleware/context'
import createEventChannel from '@store/middleware/createEventChannel'
import { setLocalUser } from '@store/slices/authSlice'
import {
  cancelVideoRecording,
  exportCapture,
  setEngineState,
  startVideoRecording,
  videoRecordingComplete
} from '@store/slices/sceneSlice'
import { PayloadType, RootState } from '@store/store'
import { EventChannel, Task } from 'redux-saga'
import {
  all,
  call,
  cancel,
  fork,
  select,
  take,
  takeEvery
} from 'redux-saga/effects'

declare global {
  interface Window {
    FULLSTORY: any
  }
}

const EXPORT_START = 'EXPORT|Download|start|export-file'
const EXPORT_SUCCESS = 'EXPORT|Download|success|export-file'
const EXPORT_ERROR = 'EXPORT|Download|error|export-file'
const EXPORT_CANCEL = 'EXPORT|Download|cancel|export-file'

function* sendUserIdentifiers() {
  const { localUser } = yield select((state: RootState) => state.auth)

  if (localUser) {
    try {
      if (window) {
        // REFACTOR GLOBAL FUNCTION
        window.FULLSTORY('setIdentity', {
          uid: localUser.uuid,
          properties: {
            displayName: localUser.adobeUserDisplayName,
            email: localUser.adobeUserEmail,
            adobeUserId: localUser.adobeUserId
          }
        })
      }
    } catch (e) {
      console.error(e)
    }
  }
}

function* handleSendEvent({
  payload
}: PayloadAction<PayloadType<typeof sendEvent>>) {
  if (!Context.LDClient?.variation('base-pf-analytics-pixel-tracking')) {
    return
  }

  try {
    switch (payload) {
      case 'landing:appBar:createProject':
        yield window?.fbq?.('track', 'StartTrial', {
          value: '0.00',
          currency: 'USD',
          predicted_ltv: '0.00'
        })
        yield window?.gtag?.('event', 'conversion', {
          // TODO: Add the correct action ID unique to the landing page
          send_to: `${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ID}/${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ACTION_DISCOVER_CREATE_PROJECT_ID}`
        })
        yield window?.ttq?.track('ClickButton')
        break
      case 'discover:appBar:createProject':
        yield window?.fbq?.('track', 'StartTrial', {
          value: '0.00',
          currency: 'USD',
          predicted_ltv: '0.00'
        })
        yield window?.gtag?.('event', 'conversion', {
          send_to: `${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ID}/${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ACTION_DISCOVER_CREATE_PROJECT_ID}`
        })
        yield window?.ttq?.track('ClickButton')
        break
      case 'landing:hero:getStarted':
        yield window?.fbq?.('track', 'ViewContent')
        yield window?.gtag?.('event', 'conversion', {
          send_to: `${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ID}/${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ACTION_LANDING_GET_STARTED_ID}`
        })
        yield window?.ttq?.track('ViewContent')
        break
      case 'landing:hero:seeHowItWorks':
        yield window?.fbq?.('track', 'ViewContent')
        yield window?.gtag?.('event', 'conversion', {
          send_to: `${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ID}/${process.env.NEXT_PUBLIC_CLIENT_ANALYTICS_GOOGLE_TAG_MANAGER_ACTION_LANDING_SEE_HOW_IT_WORKS_ID}`
        })
        yield window?.ttq?.track('ViewContent')
        break
      default:
        break
    }
  } catch (error) {
    console.error('pixel tracker event error', error)
  }
}

function* handleCaptureChannel() {
  if (!Context.Engine) return

  const dataChannel: EventChannel<EngineCapturePayload> = yield call(
    createEventChannel,
    Context.Engine?.EngineDataChannel,
    'capture_data'
  )

  while (true) {
    const { format }: EngineCapturePayload = yield take(dataChannel)
    try {
      if (
        format === 'jpg' ||
        format === 'png' ||
        format === 'svg' ||
        format === 'obj'
      ) {
        window.FULLSTORY('trackEvent', {
          name: EXPORT_SUCCESS,
          properties: {
            content_extension:format
          }
        })
      }
    } catch (err) {
      window.FULLSTORY('trackEvent', {
        name: EXPORT_ERROR,
        properties: {
          content_extension: format,
        }
      })

      captureException(err)
    }
  }
}

function* handleExportCapture({
  payload
}: PayloadAction<PayloadType<typeof exportCapture>>) {
  const { format, workflow } = payload

  if (
    (format === 'jpg' ||
      format === 'png' ||
      format === 'svg' ||
      format === 'obj') &&
    workflow === 'download'
  ) {
    window.FULLSTORY('trackEvent', {
      name: EXPORT_START,
      properties: {
        content_extension: format,
      }
    })
  }
}

function* handleStartVideoRecording() {
  window.FULLSTORY('trackEvent', {
    name: EXPORT_START,
    properties: {
      content_extension: 'mp4'
    }
  })
}

function* handleVideoRecordingComplete() {
  window.FULLSTORY('trackEvent', {
    name: EXPORT_SUCCESS,
    properties: {
      content_extension: 'mp4',
    }
  })
}

function* handleCancelVideoRecording() {
  window.FULLSTORY('trackEvent', {
    name: EXPORT_CANCEL,
    properties: {
      content_extension: 'mp4',
    }
  })
}

function* handleSetEngineState({
  payload
}: PayloadAction<PayloadType<typeof setEngineState>>) {
  let task!: Task

  if (payload === 'INITIALIZED_AND_DOCUMENT_LOADED') {
    task = yield fork(handleCaptureChannel)
  }

  if (payload === 'UNMOUNT') {
    yield cancel(task)
  }
}

export default function* monitoringSaga() {
  yield all([
    takeEvery(setLocalUser.type, sendUserIdentifiers),
    takeEvery(sendEvent.type, handleSendEvent),
    takeEvery(setEngineState.type, handleSetEngineState),
    takeEvery(exportCapture.type, handleExportCapture),
    takeEvery(startVideoRecording.type, handleStartVideoRecording),
    takeEvery(videoRecordingComplete.type, handleVideoRecordingComplete),
    takeEvery(cancelVideoRecording.type, handleCancelVideoRecording)
  ])
}
